import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useShipmentGroups } from '../../store/hooks/useShipmentGroups';
import { ShipmentGroup, ShipmentGroupOptions } from '../../models/shipment-group';
import { ShipmentGroupCard } from '../../components/shipments-groups/ShipmentGroupCard';
import { TextField, shapes, Label } from '@cimpress/react-components';
import { FlexBox, JustifyContent } from '../../utils/css-classes';
import ShipmentsSummary from '../../components/shipments-groups/ShipmentsSummary';
import SortationPage from '../../sortation/components/SortationPage';
import { FeatureToggle } from '../../components/utils/FeatureToggle';
import { Features } from '../../models/features';

// tslint:disable-next-line:import-name
import style from './index.module.css';

const Spinner = shapes.Spinner;

const isVisible = (shipmentGroup: ShipmentGroup): boolean => {
  return shipmentGroup.option === ShipmentGroupOptions.DEFAULT &&
    (shipmentGroup.shipmentCount > 0 || shipmentGroup.palletBoxes.length > 0) ||
    shipmentGroup.option === ShipmentGroupOptions.ALWAYS_VISIBLE;
};

const isPinned = (group: ShipmentGroup): boolean => group.pinned;

const inverse = <T extends any>(f: (arg: T) => boolean) => (arg: T) => !f(arg);

const sortShipmentGroups = (groupA: ShipmentGroup, groupB: ShipmentGroup) => {
  return groupA.name.localeCompare(groupB.name);
};

const isSearchResultCreator = (search: string) => {
  const regexp = new RegExp(search, 'i');
  return (shipmentGroup: ShipmentGroup) => {
    return !search.length ||
      shipmentGroup.name.search(regexp) >= 0 ||
      shipmentGroup.internalShipmentGroups.some(({ name }) => name.search(regexp) >= 0) ||
      shipmentGroup.palletBoxes.some(pb => `${pb.externalId} ${pb.name}`.search(regexp) >= 0);
  };
};

export function createShipmentsPage() {
  // tslint:disable-next-line:function-name
  return function Shipments() {
    const {
      shipmentGroups,
      getShipmentGroups,
      unsortedShipmentCount,
      sortedShipmentCount,
      isShipmentGroupsLoading,
    } = useShipmentGroups();
    const [search, setSearch] = useState('');

    const isSearchResult = useCallback(
      isSearchResultCreator(search),
      [search],
    );

    useEffect(
      () => {
        getShipmentGroups();
      },
      [],
    );

    const unknownShipmentGroups = useMemo(
      () => Object.values(shipmentGroups)
        .filter(isSearchResult)
        .filter(isPinned)
        .filter(isVisible)
        .sort(sortShipmentGroups),
      [shipmentGroups, isSearchResult],
    );

    const knownShipmentGroups = useMemo(
      () => Object.values(shipmentGroups)
        .filter(isSearchResult)
        .filter(inverse(isPinned))
        .filter(isVisible)
        .sort(sortShipmentGroups),
      [shipmentGroups, isSearchResult],
    );

    return <>
      <div className="page-title">
        <h1>Carrier Shipments</h1>
        <p>See an overview of shipment sortation into pallet boxes</p>
      </div>
      <ShipmentsSummary
        unsorted={unsortedShipmentCount}
        boxed={sortedShipmentCount}
        onTruck={'N/A'}
      />
      <div>
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          label="Search"
        />
      </div>
      {!isShipmentGroupsLoading && <>
        {unknownShipmentGroups.length > 0 && <div>
          <h2>Unknown Shipment Groups <Label text={`${unknownShipmentGroups.length}`}/></h2>
          {unknownShipmentGroups.map(
            (group, i) => <ShipmentGroupCard key={i} shipmentGroup={group} highlight={search} />,
          )}
          <hr className={style.hr}/>
        </div>}
        <div>
          <h2>Shipment Groups <Label text={`${knownShipmentGroups.length}`}/></h2>
          {knownShipmentGroups.map(
            (group, i) => <ShipmentGroupCard key={i} shipmentGroup={group} highlight={search} />,
          )}
        </div>
      </> || <Spinner className={`${FlexBox.FLEX} ${JustifyContent.CENTER}`} />}
    </>;
  };
}

// tslint:disable-next-line:variable-name
const Shipments = createShipmentsPage();

export default function () {
  return <>
    <FeatureToggle whenFlag={Features.CUSTOM_SHIPMENT_GROUPS} isEnabled>
      <Shipments/>
    </FeatureToggle>
    <FeatureToggle whenFlag={Features.CUSTOM_SHIPMENT_GROUPS} isDisabled>
      <SortationPage/>
    </FeatureToggle>
  </>;
}
